import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { isAllowedIntakeUrl } from './CredentialIntakeSheet'
import { useEcwCredentialIntakeMutation } from './useCredentialIntakeMutation'

export function useCredentialIntakeSheet(
  refetchCredentialStatus: () => Promise<unknown>
) {
  const intake = useEcwCredentialIntakeMutation()
  const [open, setOpen] = useState(false)
  const [targetName, setTargetName] = useState<string | null>(null)
  const activeTargetName = useRef<string | null>(null)
  const launchedTargetName = useRef<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    if (!open || !targetName || launchedTargetName.current === targetName)
      return
    launchedTargetName.current = targetName
    const launchTarget = targetName

    const launch = async () => {
      try {
        const link = await intake.mutateAsync()
        if (activeTargetName.current !== launchTarget) return
        const intakeUrl = new URL(link.intake_url, window.location.href)
        if (!isAllowedIntakeUrl(intakeUrl)) {
          throw new Error('Credential intake URL is not allowed')
        }
        const form = document.createElement('form')
        const contract = document.createElement('input')
        form.method = 'POST'
        form.action = intakeUrl.href
        form.target = launchTarget
        form.hidden = true
        contract.type = 'hidden'
        contract.name = 'contract'
        contract.value = link.contract
        form.append(contract)
        document.body.append(form)
        try {
          form.submit()
        } finally {
          form.remove()
        }
      } catch {
        if (activeTargetName.current !== launchTarget) return
        activeTargetName.current = null
        setOpen(false)
        setTargetName(null)
        toast.error('Credential intake is unavailable. Try again.')
      }
    }

    void launch()
  }, [intake, open, targetName])

  const openSheet = () => {
    const nextTargetName = `careforce-credential-intake-${Date.now()}`
    activeTargetName.current = nextTargetName
    launchedTargetName.current = null
    setTargetName(nextTargetName)
    setOpen(true)
  }

  const onOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) return
      const wasOpen = activeTargetName.current !== null
      activeTargetName.current = null
      setOpen(false)
      setTargetName(null)
      if (wasOpen) void refetchCredentialStatus()
    },
    [refetchCredentialStatus]
  )

  useEffect(() => {
    if (!open) return

    const handleMessage = (event: MessageEvent<unknown>) => {
      const intakeWindow = iframeRef.current?.contentWindow
      if (!intakeWindow || event.source !== intakeWindow) return
      let origin: URL
      try {
        origin = new URL(event.origin)
      } catch {
        return
      }
      if (!isAllowedIntakeUrl(origin)) return
      if (
        typeof event.data !== 'object' ||
        event.data === null ||
        !('type' in event.data) ||
        event.data.type !== 'careforce:credential-intake:close'
      ) {
        return
      }
      onOpenChange(false)
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [onOpenChange, open])

  return {
    iframeRef,
    isLaunching: intake.isPending,
    onOpenChange,
    open,
    openSheet,
    targetName,
  }
}
